import {createApi} from '@reduxjs/toolkit/query/react';

import {isNotNil} from 'ramda';

import {createWorkspaceBaseQuery, setWorktreeHeader} from '@dms/api/base';
import {
  ChangePasswordApiArg,
  ChangePasswordApiResponse,
  ExternalLoginApiArg,
  ExternalLoginApiResponse,
  GetAvailableLanguagesListApiArg,
  GetAvailableLanguagesListApiResponse,
  LoginApiArg,
  LoginApiResponse,
  PostLogoutApiArg,
  RefreshTokenApiArg,
  RefreshTokenApiResponse,
  RequestPasswordResetApiArg,
  RequestPasswordResetApiResponse,
  VerifyTokenApiArg,
  VerifyTokenApiResponse,
  WorkspaceInfoApiArg,
  WorkspaceInfoApiResponse,
  WorkspacesApiArg,
  WorkspacesApiResponse,
} from '@dms/api/shared';

import {buildObject} from 'shared/web';

const workspaceBaseQuery = createWorkspaceBaseQuery({
  baseUrl: process.env.API_URL,
  prepareHeaders: setWorktreeHeader,
});

export const publicApi = createApi({
  reducerPath: 'rtk_publicApi',
  baseQuery: workspaceBaseQuery,
  endpoints: (build) => ({
    externalLogin: build.mutation<ExternalLoginApiResponse, ExternalLoginApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auth/login-external`,
        workspace: queryArg.workspace,
        method: 'POST',
        body: queryArg.externalLoginRequestBody,
      }),
    }),
    login: build.mutation<LoginApiResponse, LoginApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auth/login`,
        workspace: queryArg.workspace,
        method: 'POST',
        body: queryArg.loginRequestBody,
      }),
    }),
    changePassword: build.mutation<ChangePasswordApiResponse, ChangePasswordApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auth/change-password-by-token`,
        workspace: queryArg.workspace,
        method: 'PUT',
        body: queryArg.changePasswordRequestBody,
      }),
    }),
    requestPasswordReset: build.mutation<
      RequestPasswordResetApiResponse,
      RequestPasswordResetApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/auth/request-password-reset`,
        workspace: queryArg.workspace,
        method: 'PUT',
        body: queryArg.requestPasswordResetRequestBody,
      }),
    }),
    verifyToken: build.mutation<VerifyTokenApiResponse, VerifyTokenApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auth/verify-password-reset-token`,
        workspace: queryArg.workspace,
        method: 'PUT',
        body: queryArg.verifyTokenRequestBody,
      }),
    }),
    refreshToken: build.mutation<RefreshTokenApiResponse, RefreshTokenApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/auth/token/refresh`,
        workspace: queryArg.workspace,
        method: 'POST',
        body: queryArg.refreshTokenRequestBody,
      }),
    }),
    workspaceInfo: build.query<WorkspaceInfoApiResponse, WorkspaceInfoApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workspace/info`,
        workspace: queryArg.workspace,
        method: 'GET',
      }),
    }),
    workspaces: build.query<WorkspacesApiResponse, WorkspacesApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workspaces`,
        workspace: queryArg.workspace,
        params: {email: queryArg.email},
      }),
    }),
    getAvailableLanguagesList: build.query<
      GetAvailableLanguagesListApiResponse,
      GetAvailableLanguagesListApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/available-languages`,
        workspace: queryArg.workspace,
      }),
    }),
    logout: build.mutation<void, PostLogoutApiArg>({
      query: ({accessToken, workspace}) => ({
        url: `/dms/v1/auth/logout`,
        workspace,
        method: 'POST',
        headers: buildObject<{Credentials: string; Authorization: string}>()
          .Credentials('include')
          .Authorization(`Bearer ${accessToken ?? ''}`, isNotNil(accessToken))
          .build(),
      }),
    }),
  }),
});

export const {
  useLogoutMutation,
  useWorkspacesQuery,
  useChangePasswordMutation,
  useExternalLoginMutation,
  useGetAvailableLanguagesListQuery,
  useLoginMutation,
  useWorkspaceInfoQuery,
  useLazyWorkspaceInfoQuery,
  useVerifyTokenMutation,
  useRequestPasswordResetMutation,
  useRefreshTokenMutation,
} = publicApi;
